import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { io } from 'socket.io-client';
import { officeAPI, cityCodeAPI } from '../../services/api';
import { format } from 'date-fns';
import { CitySelect, ArabicDatePicker } from '../../components/SearchWidgets';
const fmtDate = (d) => d ? format(new Date(d), 'yyyy/MM/dd HH:mm') : '—';

const STEPS = ['البحث عن رحلة', 'اختيار المقاعد', 'بيانات المسافر', 'الدفع', 'تأكيد'];

// ── Seat map helpers ──────────────────────────────────────────────────────────
const BUS_CONFIGS = {
  '2+2': { left: 2, right: 2 },
  '2+1': { left: 2, right: 1 },
  '1+1': { left: 1, right: 1 },
};

const getBusLayoutCfg = (bus) => {
  if (bus?.seatLayout?.config) return BUS_CONFIGS[bus.seatLayout.config] || BUS_CONFIGS['2+2'];
  const spr = bus?.seatLayout?.seatsPerRow;
  if (spr === 3) return BUS_CONFIGS['2+1'];
  if (spr === 2) return BUS_CONFIGS['1+1'];
  return BUS_CONFIGS['2+2'];
};

const toArNum = (n) => String(n).replace(/[0-9]/g, d => '٠١٢٣٤٥٦٧٨٩'[d]);

const SeatBtn = ({ n, isBooked, isSelected, onToggle, small = false }) => {
  const state = isBooked ? 'booked' : isSelected ? 'selected' : 'free';
  const w = small ? 44 : 52;
  const h = small ? 50 : 56;
  return (
    <button
      type="button"
      onClick={() => !isBooked && onToggle(n)}
      disabled={isBooked}
      title={`مقعد ${toArNum(n)}`}
      aria-pressed={isSelected}
      className={`relative shrink-0 flex items-center justify-center select-none transition-all duration-150 ${
        isBooked   ? 'cursor-not-allowed' :
        isSelected ? 'cursor-pointer active:scale-95' :
                     'cursor-pointer hover:scale-105 active:scale-95'
      }`}
      style={{
        width: w, height: h,
        background: state === 'selected' ? '#dcfce7' : '#ffffff',
        borderRadius: 10,
        border: state === 'selected' ? '3px solid #15803d'
              : state === 'booked'   ? '2px solid #f97316'
              :                        '2px solid #d1d5db',
        boxShadow: state === 'selected'
          ? '0 0 0 4px rgba(21,128,61,0.40)'
          : state === 'booked'
          ? '0 0 0 2px rgba(249,115,22,0.18)'
          : '0 2px 6px rgba(0,0,0,0.10)',
        padding: 0,
      }}
    >
      <span style={{
        fontSize: 16, fontWeight: 800, lineHeight: 1,
        color: state === 'selected' ? '#15803d' : state === 'booked' ? '#9ca3af' : '#374151',
        fontFamily: 'Cairo, sans-serif', userSelect: 'none',
      }}>
        {toArNum(n)}
      </span>
      {isBooked && (
        <span style={{ position: 'absolute', top: 3, right: 3 }}>
          <svg width="13" height="14" viewBox="0 0 13 14" fill="none">
            <rect x="1.5" y="6" width="10" height="7.5" rx="2" fill="#f97316"/>
            <path d="M 3.5 6 V 4.5 a 3 3 0 0 1 6 0 V 6"
              stroke="#f97316" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
            <circle cx="6.5" cy="9.8" r="1.3" fill="#ffffff"/>
          </svg>
        </span>
      )}
    </button>
  );
};

const SeatDiagram = ({ bookedSeats = [], selectedSeats, onToggle, bus, totalSeats: propTotalSeats }) => {
  const totalSeats  = bus?.totalSeats || propTotalSeats || 45;
  const cfg         = getBusLayoutCfg(bus);
  const seatsPerRow = cfg.left + cfg.right;

  const BACK_ROW_SIZE  = bus?.seatLayout?.backRowSize ?? 5;
  const hasBackRow     = BACK_ROW_SIZE > 0 && totalSeats > BACK_ROW_SIZE;
  const mainSeatCount  = hasBackRow ? totalSeats - BACK_ROW_SIZE : totalSeats;

  const rows = [];
  let seat = 1;
  while (seat <= mainSeatCount) {
    const left  = Array.from({ length: cfg.left  }, () => seat <= mainSeatCount ? seat++ : null).filter(Boolean);
    const right = Array.from({ length: cfg.right }, () => seat <= mainSeatCount ? seat++ : null).filter(Boolean);
    if (left.length || right.length) rows.push({ left, right });
  }
  const backSeats = hasBackRow
    ? Array.from({ length: BACK_ROW_SIZE }, (_, i) => mainSeatCount + 1 + i)
    : [];

  const exitAfterRow = Math.floor(rows.length * 0.5);

  const S = (n) => (
    <SeatBtn key={n} n={n}
      isBooked={bookedSeats.includes(n)}
      isSelected={selectedSeats.includes(n)}
      onToggle={onToggle}
    />
  );

  return (
    <div dir="ltr" className="select-none">
      {/* Legend */}
      <div dir="rtl" className="flex items-center justify-center gap-5 mb-3">
        <div className="flex items-center gap-1.5">
          <div style={{ width:18, height:18, borderRadius:4, background:'#ffffff', border:'2px solid #d1d5db', boxShadow:'0 1px 3px rgba(0,0,0,0.10)' }} />
          <span className="text-xs text-gray-500">متاح</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div style={{ width:18, height:18, borderRadius:4, background:'#dcfce7', border:'3px solid #15803d', boxShadow:'0 0 0 3px rgba(21,128,61,0.35)' }} />
          <span className="text-xs text-gray-500">محدد</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div style={{ width:18, height:18, borderRadius:4, background:'#ffffff', border:'2px solid #f97316', boxShadow:'0 0 0 2px rgba(249,115,22,0.15)', position:'relative', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <svg width="9" height="10" viewBox="0 0 9 10" fill="none">
              <rect x="1" y="3.8" width="7" height="5.8" rx="1.5" fill="#f97316"/>
              <path d="M 2.2 3.8 V 2.8 a 2.3 2.3 0 0 1 4.6 0 v 1" stroke="#f97316" strokeWidth="1.4" fill="none" strokeLinecap="round"/>
            </svg>
          </div>
          <span className="text-xs text-gray-500">محجوز</span>
        </div>
      </div>

      {/* Direction label */}
      <div className="flex items-center justify-end gap-1 mb-2 pr-1" dir="rtl">
        <span className="text-[11px] text-gray-600 font-semibold">أمام الباص</span>
        <span className="text-gray-500 text-xs">→</span>
      </div>

      {/* Bus body */}
      <div className="relative mx-auto overflow-hidden" style={{
        width: 270, background: '#12192b', borderRadius: 36, padding: '14px 10px',
        boxShadow: ['inset 0 0 0 4px #2d3748','inset 0 0 0 7px #1a202c','0 0 0 3px #4a5568','0 0 0 5px #718096','0 8px 40px rgba(0,0,0,0.6)'].join(', '),
      }}>
        {/* LED strips */}
        <div style={{ position:'absolute', left:5, top:90, bottom:90, width:5, borderRadius:3, background:'linear-gradient(180deg,#3b82f6 0%,#1d4ed8 50%,#3b82f6 100%)', opacity:0.85 }} />
        <div style={{ position:'absolute', right:5, top:90, bottom:90, width:5, borderRadius:3, background:'linear-gradient(180deg,#3b82f6 0%,#1d4ed8 50%,#3b82f6 100%)', opacity:0.85 }} />

        {/* Dashboard SVG */}
        <svg width="250" height="176" viewBox="0 0 250 176" fill="none" style={{ display:'block', marginBottom:8 }}>
          <rect width="250" height="176" rx="13" fill="#080c15"/>
          <rect x="0" y="0" width="250" height="28" fill="#0b1020"/>
          <rect x="0" y="17" width="250" height="11" fill="#080c15"/>
          {[0,1,2,3,4,5].map(i => <rect key={'lg'+i} x="10" y={5+i*4} width="82" height="2" rx="1" fill="#162030"/>)}
          <rect x="106" y="4" width="38" height="20" rx="5" fill="#060912"/>
          <rect x="110" y="7" width="30" height="14" rx="4" fill="#03060c"/>
          <circle cx="125" cy="14" r="6" fill="#020509"/>
          <circle cx="125" cy="14" r="3.5" fill="#0c1525"/>
          <circle cx="123.5" cy="12.5" r="0.8" fill="#1c2b40"/>
          {[0,1,2,3,4,5].map(i => <rect key={'rg'+i} x="158" y={5+i*4} width="82" height="2" rx="1" fill="#162030"/>)}
          <circle cx="75" cy="77" r="43" fill="#000" opacity="0.3"/>
          <circle cx="75" cy="75" r="43" fill="#0d1522" stroke="#192434" strokeWidth="9"/>
          <circle cx="75" cy="75" r="33" fill="none" stroke="#101928" strokeWidth="1.5"/>
          <line x1="75" y1="32"  x2="75"  y2="62"  stroke="#0a1420" strokeWidth="9" strokeLinecap="round"/>
          <line x1="38"  y1="98" x2="61"  y2="86"  stroke="#0a1420" strokeWidth="9" strokeLinecap="round"/>
          <line x1="112" y1="98" x2="89"  y2="86"  stroke="#0a1420" strokeWidth="9" strokeLinecap="round"/>
          <circle cx="75" cy="75" r="16" fill="#0e1a2a"/>
          <circle cx="75" cy="75" r="10" fill="#111d2e"/>
          <circle cx="75" cy="75" r="5.5" fill="#080e1c"/>
          <path d="M 44 49 A 42 42 0 0 1 106 49" stroke="#213248" strokeWidth="2" fill="none" strokeLinecap="round"/>
          <rect x="132" y="28" width="112" height="86" rx="8" fill="#080e1c"/>
          {[0,1,2,3,4,5,6,7].map(i => <rect key={'vl'+i} x="140" y={34+i*6} width="96" height="2.5" rx="1.25" fill="#101a2c"/>)}
          <rect x="140" y="86" width="96" height="22" rx="6" fill="#060a12"/>
          <rect x="144" y="89" width="88" height="16" rx="4" fill="#040810"/>
          <rect x="148" y="93" width="32" height="3" rx="1.5" fill="#0e1826"/>
          <rect x="148" y="98" width="52" height="2.5" rx="1.25" fill="#0c1523"/>
          <line x1="4" y1="118" x2="246" y2="118" stroke="#0d1726" strokeWidth="1"/>
          <rect x="6"   y="120" width="44" height="20" rx="6" fill="#1a2636"/>
          <rect x="52"  y="120" width="14" height="16" rx="3" fill="#05080e"/>
          <rect x="68"  y="120" width="44" height="20" rx="6" fill="#1a2636"/>
          <rect x="4"   y="142" width="112" height="22" rx="8" fill="#162232"/>
          <rect x="9"   y="146" width="102" height="14" rx="6" fill="#1d2c3f"/>
          <rect x="9"   y="155" width="102" height="3"  rx="1.5" fill="#0f1b2b"/>
          <rect x="6"   y="165" width="108" height="7"  rx="3" fill="#111d2c"/>
          <text x="60" y="156" textAnchor="middle" fontSize="10" fill="#7a8fa6" fontFamily="Cairo, Arial, sans-serif" fontWeight="700">السائق</text>
          <rect x="118" y="118" width="14" height="55" rx="3" fill="#040710"/>
          <rect x="121" y="124" width="8"  height="7"  rx="2" fill="#0a1320"/>
          <rect x="121" y="135" width="8"  height="4"  rx="1.5" fill="#0a1320"/>
          <rect x="121" y="143" width="8"  height="4"  rx="1.5" fill="#0a1320"/>
          <rect x="122" y="151" width="6"  height="14" rx="2" fill="#08111e"/>
          <rect x="134" y="120" width="44" height="20" rx="6" fill="#1a2636"/>
          <rect x="180" y="120" width="14" height="16" rx="3" fill="#05080e"/>
          <rect x="196" y="120" width="44" height="20" rx="6" fill="#1a2636"/>
          <rect x="132" y="142" width="112" height="22" rx="8" fill="#162232"/>
          <rect x="137" y="146" width="102" height="14" rx="6" fill="#1d2c3f"/>
          <rect x="137" y="155" width="102" height="3"  rx="1.5" fill="#0f1b2b"/>
          <rect x="134" y="165" width="108" height="7"  rx="3" fill="#111d2c"/>
          <text x="188" y="156" textAnchor="middle" fontSize="10" fill="#7a8fa6" fontFamily="Cairo, Arial, sans-serif" fontWeight="700">مساعد السائق</text>
          <rect x="0"   y="120" width="4" height="56" rx="2" fill="#1e4a8a" opacity="0.8"/>
          <rect x="246" y="120" width="4" height="56" rx="2" fill="#1e4a8a" opacity="0.8"/>
        </svg>

        {/* Passenger rows */}
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {rows.map(({ left, right }, ri) => (
            <React.Fragment key={ri}>
              {ri === exitAfterRow && (
                <div style={{ display:'flex', alignItems:'center', gap:4, padding:'4px 0', margin:'2px 0' }}>
                  <div style={{ flex:1, height:1, background:'rgba(251,191,36,0.25)' }} />
                  <span style={{ fontSize:9, color:'rgba(251,191,36,0.6)', whiteSpace:'nowrap', fontFamily:'Cairo, sans-serif' }}>مخرج طوارئ</span>
                  <div style={{ flex:1, height:1, background:'rgba(251,191,36,0.25)' }} />
                </div>
              )}
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:0 }}>
                <div style={{ display:'flex', gap:5 }}>{left.map(n => S(n))}</div>
                <div style={{ width:28, alignSelf:'stretch', minHeight:56, background:'#ffffff', margin:'0 5px', borderRadius:3, opacity:0.08 }} />
                <div style={{ display:'flex', gap:5 }}>{right.map(n => S(n))}</div>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Back bench — small seats so all fit inside the 250 px inner width */}
        {backSeats.length > 0 && (
          <div style={{ marginTop:10, paddingTop:10, borderTop:'1px solid rgba(255,255,255,0.08)', display:'flex', justifyContent:'center', gap:3 }}>
            {backSeats.map(n => (
              <SeatBtn key={n} n={n}
                isBooked={bookedSeats.includes(n)}
                isSelected={selectedSeats.includes(n)}
                onToggle={onToggle}
                small
              />
            ))}
          </div>
        )}
      </div>

      {/* Direction label bottom */}
      <div className="flex items-center justify-end gap-1 mt-2 pr-1" dir="rtl">
        <span className="text-[11px] text-gray-600 font-semibold">مؤخرة الباص</span>
        <span className="text-gray-500 text-xs">→</span>
      </div>
    </div>
  );
};

export default function OfficeNewBookingPage() {
  const navigate = useNavigate();

  // Step
  const [step, setStep]       = useState(0);

  // Step 0 — trip search
  const [cities, setCities]   = useState([]);
  const [from, setFrom]       = useState('');
  const [to, setTo]           = useState('');
  const [date, setDate]       = useState('');
  const [trips, setTrips]     = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    // Super admin's curated city list (see OfficeNewParcelPage) — always
    // populated, unlike officeAPI.getCities() which derives from currently-
    // scheduled trips and comes back empty whenever none are scheduled yet.
    cityCodeAPI.getAll()
      .then((res) => setCities((res.data.data || []).map((c) => c.name).sort((a, b) => a.localeCompare(b, 'ar'))))
      .catch(() => {});
  }, []);

  // Step 1 — seat selection
  const [trip, setTrip]           = useState(null);
  const [seatData, setSeatData]   = useState(null);
  const [selected, setSelected]   = useState([]);
  const [loadingSeats, setLoadingSeats] = useState(false);
  // Seats another viewer (online or a different office terminal) has merely
  // tapped on this same trip right now — soft hold via seatHoldService.js.
  const [heldSeats, setHeldSeats] = useState([]);
  const socketRef = useRef(null);

  // Step 2 — passenger
  const [mobile, setMobile]     = useState('');
  const [pName, setPName]       = useState('');
  const [looking, setLooking]   = useState(false);
  const [found, setFound]       = useState(null); // existing customer

  // Step 3 — payment
  const [bankAccounts, setBankAccounts]   = useState([]);
  const [slip, setSlip]                   = useState(null);
  const [slipPreview, setSlipPreview]     = useState(null);
  const [uploadingSlip, setUploadingSlip] = useState(false);

  // Step 4 — result
  const [booking, setBooking]   = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // ── Step 0: search trips ───────────────────────────────────────────────────
  const searchTrips = async (e) => {
    e.preventDefault();
    if (!from && !to) return toast.warning('اختر مدينة الانطلاق أو الوجهة على الأقل');
    if (from && to && from === to) return toast.warning('مدينة الانطلاق والوجهة لا يمكن أن تكون نفسها');
    setSearching(true);
    try {
      const res = await officeAPI.searchTrips({ fromCity: from, toCity: to, date });
      setTrips(res.data.data || []);
      if ((res.data.data || []).length === 0) toast.info('لا توجد رحلات متاحة بهذه المعايير');
    } catch { toast.error('خطأ في البحث'); }
    finally { setSearching(false); }
  };

  const selectTrip = async (t) => {
    if (loadingSeats) return;
    setLoadingSeats(true);
    setTrip(t);
    setSelected([]);
    try {
      const res = await officeAPI.getTripSeats(t.id);
      setSeatData(res.data.data);
      setStep(1);
    } catch { toast.error('خطأ في تحميل المقاعد'); }
    finally { setLoadingSeats(false); }
  };

  // Live seat-conflict guard — same trip:${id} broadcast TripTrackingPage.jsx
  // and PassengerBookPage.jsx consume (seatUpdateService.js server-side), so
  // a seat another office/online booking just took disappears from this grid
  // immediately instead of only surfacing as a 400 at final submit.
  useEffect(() => {
    if (!trip?.id) return;
    const serverUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');
    const socket = io(serverUrl, { transports: ['websocket', 'polling'], withCredentials: true });
    socketRef.current = socket;
    setHeldSeats([]);

    socket.on('connect', () => socket.emit('passenger:trackTrip', trip.id));

    socket.on('trip:seatsUpdated', (data) => {
      if (data.tripId !== trip.id) return;
      setSeatData((prev) => prev && ({ ...prev, bookedSeats: data.bookedSeats, availableSeats: data.availableSeats }));
      setSelected((prev) => {
        const lost = prev.filter((s) => data.bookedSeats?.includes(s));
        if (lost.length) toast.warning('تم حجز أحد المقاعد التي اخترتها من راكب آخر — فضلاً اختر مقعداً آخر');
        return prev.filter((s) => !data.bookedSeats?.includes(s));
      });
    });

    // Someone else's tap, not a real booking yet — gray the seat out here
    // too so two terminals/channels can't both select it.
    socket.on('trip:seatHoldsSnapshot', (data) => {
      if (data.tripId === trip.id) setHeldSeats((prev) => [...new Set([...prev, ...(data.heldSeats || [])])]);
    });
    socket.on('trip:seatHeld', (data) => {
      if (data.tripId === trip.id) setHeldSeats((prev) => prev.includes(data.seatNumber) ? prev : [...prev, data.seatNumber]);
    });
    socket.on('trip:seatReleased', (data) => {
      if (data.tripId === trip.id) setHeldSeats((prev) => prev.filter((s) => s !== data.seatNumber));
    });
    socket.on('trip:seatSelectRejected', (data) => {
      if (data.tripId !== trip.id) return;
      setSelected((prev) => prev.filter((s) => s !== data.seatNumber));
      setHeldSeats((prev) => prev.includes(data.seatNumber) ? prev : [...prev, data.seatNumber]);
      toast.warning('تم اختيار هذا المقعد من راكب آخر — فضلاً اختر مقعداً آخر');
    });

    return () => socket.disconnect();
  }, [trip?.id]);

  // ── Step 1: seat selection ─────────────────────────────────────────────────
  const toggleSeat = (n) => {
    const unavailable = [...(seatData?.bookedSeats || []), ...(seatData?.blockedSeats || []), ...heldSeats];
    if (unavailable.includes(n)) return;
    setSelected(s => {
      if (s.includes(n)) {
        socketRef.current?.emit('seat:deselect', { tripId: trip.id, seatNumber: n });
        return s.filter(x => x !== n);
      }
      if (s.length >= 9) return s;
      // Broadcast the soft hold immediately so other channels gray this
      // seat out right away (seatHoldService.js).
      socketRef.current?.emit('seat:select', { tripId: trip.id, seatNumber: n });
      return [...s, n];
    });
  };

  // ── Step 2: passenger lookup ───────────────────────────────────────────────
  const lookupMobile = async () => {
    if (!mobile || mobile.replace(/\D/g, '').length < 9) {
      return toast.warning('أدخل رقم هاتف صحيح');
    }
    setLooking(true);
    try {
      const res = await officeAPI.lookupCustomer({ mobile });
      if (res.data.data.found) {
        setFound(res.data.data);
        setPName(res.data.data.name);
        toast.success('تم العثور على العميل');
      } else {
        setFound(null);
        setPName('');
        toast.info('عميل جديد — أدخل الاسم');
      }
    } catch { toast.error('خطأ في البحث'); }
    finally { setLooking(false); }
  };

  // Name must have at least 4 words (Arabic quadruple name standard)
  const nameParts = pName.trim().split(/\s+/).filter(Boolean);
  const nameValid = nameParts.length >= 4;

  // ── Step 2 → 3: create booking, then go to payment step ──────────────────
  const confirmBooking = async () => {
    if (!pName.trim()) return toast.error('اسم المسافر مطلوب');
    if (!nameValid) return toast.error('يجب إدخال الاسم الرباعي كاملاً كما هو في بطاقة الهوية');
    setSubmitting(true);
    try {
      const res = await officeAPI.createBooking({
        tripId: trip.id,
        seats: selected,
        passenger: { mobile, name: pName.trim() },
      });
      setBooking(res.data.data);
      setBankAccounts(res.data.data.bankAccounts || []);
      setStep(3); // → payment step
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في إنشاء الحجز');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Step 3 → 4: upload payment slip ───────────────────────────────────────
  const submitPayment = async () => {
    if (!slip) return toast.warning('يرجى اختيار صورة إيصال الدفع');
    setUploadingSlip(true);
    try {
      const fd = new FormData();
      fd.append('slip', slip);
      await officeAPI.submitPaymentSlip(booking.bookingId, fd);
      setStep(4); // → pending review
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في رفع الإيصال');
    } finally {
      setUploadingSlip(false);
    }
  };

  const handleSlipChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSlip(file);
    if (file.type.startsWith('image/')) {
      setSlipPreview(URL.createObjectURL(file));
    } else {
      setSlipPreview(null); // PDF — no preview
    }
  };

  const totalAmount = trip ? parseFloat(trip.price) * selected.length : 0;

  return (
    <div dir="rtl" className="p-4 md:p-6 max-w-2xl mx-auto">
      {/* Breadcrumb step indicator */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1">
        {STEPS.map((s, i) => (
          <React.Fragment key={i}>
            <div className={`flex items-center gap-1.5 shrink-0 ${i === step ? 'text-blue-700' : i < step ? 'text-emerald-600' : 'text-gray-400'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 ${i === step ? 'bg-blue-600 text-white border-blue-600' : i < step ? 'bg-emerald-500 text-white border-emerald-500' : 'border-gray-300'}`}>
                {i < step ? '✓' : i + 1}
              </div>
              <span className="text-xs font-medium whitespace-nowrap">{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-gray-200 min-w-4" />}
          </React.Fragment>
        ))}
      </div>

      {/* ── Step 0: Search — same look as the passenger search card ─── */}
      {step === 0 && (
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-5">البحث عن رحلة</h2>
          <form onSubmit={searchTrips} className="bg-white rounded-[20px] shadow-[0_20px_60px_rgba(10,30,82,.15)] border border-gray-100 p-6 sm:p-7 mb-5">
            <div className="grid grid-cols-1 sm:grid-cols-[1.5fr_44px_1.5fr] gap-3 items-end mb-4">
              {/* من */}
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1.5 mr-0.5">من</label>
                <CitySelect
                  value={from}
                  onChange={setFrom}
                  cities={cities}
                  icon={
                    <svg className="w-5 h-5 text-red-500" viewBox="0 0 24 24" fill="currentColor">
                      <path fillRule="evenodd" d="M11.54 22.351l.07.04.028.016a.76.76 0 00.723 0l.028-.015.07-.041a16.975 16.975 0 001.144-.742 19.58 19.58 0 002.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 00-16.5 0c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 002.682 2.282 16.975 16.975 0 001.145.742zM12 13.5a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                    </svg>
                  }
                />
              </div>

              {/* Swap */}
              <div className="hidden sm:flex items-end justify-center pb-0.5">
                <button type="button" onClick={() => { setFrom(to); setTo(from); }}
                  className="w-10 h-[52px] bg-gray-50 hover:bg-blue-50 hover:text-[#1E40AF] border-2 border-gray-200 hover:border-blue-200 text-gray-400 rounded-xl flex items-center justify-center transition-all text-base font-bold"
                  title="تبديل المدينتين">
                  ⇆
                </button>
              </div>

              {/* إلى */}
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1.5 mr-0.5">إلى</label>
                <CitySelect
                  value={to}
                  onChange={setTo}
                  cities={cities.filter((c) => c !== from)}
                  icon={
                    <svg className="w-5 h-5 text-[#1E3A8A]" viewBox="0 0 24 24" fill="currentColor">
                      <path fillRule="evenodd" d="M3 2.25a.75.75 0 01.75.75v.54l1.838-.46a9.75 9.75 0 016.725.738l.108.054a8.25 8.25 0 005.58.652l3.109-.732a.75.75 0 01.917.81 47.784 47.784 0 00.005 10.337.75.75 0 01-.574.812l-3.114.733a9.75 9.75 0 01-6.594-.77l-.108-.054a8.25 8.25 0 00-5.69-.625l-2.202.55V21a.75.75 0 01-1.5 0V3A.75.75 0 013 2.25z" clipRule="evenodd" />
                    </svg>
                  }
                />
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-xs font-bold text-gray-600 mb-1.5 mr-0.5">
                التاريخ <span className="text-gray-400 font-normal">(اختياري)</span>
              </label>
              <ArabicDatePicker
                value={date}
                onChange={setDate}
                min={new Date().toISOString().split('T')[0]}
                placeholder="أي تاريخ"
                allowClear
              />
            </div>

            <button type="submit" disabled={searching}
              className="w-full h-[56px] bg-[#1E40AF] hover:bg-[#1D4ED8] disabled:opacity-60 text-white rounded-xl text-base font-black transition-all shadow-[0_6px_20px_rgba(30,64,175,.3)] hover:shadow-[0_10px_28px_rgba(30,64,175,.4)] flex items-center justify-center gap-3">
              {searching ? 'جاري البحث...' : 'بحث عن رحلات'}
            </button>
          </form>

          {trips.length > 0 && (
            <div className="space-y-3">
              {trips.map((t) => (
                <div key={t.id} className="bg-white rounded-2xl border border-gray-200 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:border-blue-300 hover:shadow-sm transition">
                  <div>
                    <p className="font-bold text-gray-900">{t.fromCity} ← {t.toCity}</p>
                    <p className="text-xs text-gray-500 mt-1">{fmtDate(t.departureTime)} · {t.company?.name}</p>
                    <p className="text-xs text-gray-500">{t.availableSeats} مقعد متاح</p>
                  </div>
                  <div className="flex items-center justify-between sm:flex-col sm:items-end gap-2">
                    <p className="font-bold text-blue-700 text-lg">{parseFloat(t.price).toLocaleString()} <span className="text-xs font-normal text-gray-400">ج.س</span></p>
                    <button
                      onClick={() => selectTrip(t)}
                      disabled={loadingSeats}
                      className="sm:mt-2 bg-blue-600 text-white px-4 py-2 sm:py-1.5 rounded-xl text-xs font-bold hover:bg-blue-700 disabled:opacity-50 transition"
                    >
                      {loadingSeats && trip?.id === t.id ? '...' : 'اختيار'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Step 1: Seat map ─── */}
      {step === 1 && trip && seatData && (
        <div>
          <button onClick={() => setStep(0)} className="text-sm text-gray-500 hover:text-gray-800 mb-4 flex items-center gap-1">
            ← عودة
          </button>
          <h2 className="text-xl font-bold text-gray-900 mb-1">اختيار المقاعد</h2>
          <p className="text-sm text-gray-500 mb-5">{trip.fromCity} ← {trip.toCity} · {fmtDate(trip.departureTime)}</p>

          {/* Seat diagram */}
          <div className="bg-white rounded-2xl border p-5 mb-4 flex justify-center">
            <SeatDiagram
              bookedSeats={[...(seatData.bookedSeats || []), ...(seatData.blockedSeats || []), ...heldSeats]}
              selectedSeats={selected}
              onToggle={toggleSeat}
              bus={seatData.bus}
              totalSeats={seatData.totalSeats}
            />
          </div>

          {selected.length > 0 && (
            <div className="bg-blue-50 rounded-2xl p-4 mb-4">
              <p className="text-sm font-semibold text-blue-800">المقاعد المختارة: {[...selected].sort((a,b)=>a-b).join('، ')}</p>
              <p className="text-sm text-blue-600 mt-1">الإجمالي: {totalAmount.toLocaleString()} ج.س ({selected.length} × {parseFloat(trip.price).toLocaleString()})</p>
            </div>
          )}

          <button
            disabled={selected.length === 0}
            onClick={() => setStep(2)}
            className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 disabled:opacity-40 transition"
          >
            التالي — بيانات المسافر
          </button>
        </div>
      )}

      {/* ── Step 2: Passenger info ─── */}
      {step === 2 && (
        <div>
          <button onClick={() => setStep(1)} className="text-sm text-gray-500 hover:text-gray-800 mb-4 flex items-center gap-1">
            ← عودة
          </button>
          <h2 className="text-xl font-bold text-gray-900 mb-5">بيانات المسافر</h2>

          <div className="bg-white rounded-2xl border p-5 space-y-4">
            {/* Mobile lookup */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">رقم الهاتف *</label>
              <div className="flex gap-2">
                <input
                  dir="ltr"
                  className="flex-1 border rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="+249912345678"
                  value={mobile}
                  onChange={(e) => { setMobile(e.target.value); setFound(null); setPName(''); }}
                />
                <button
                  type="button"
                  onClick={lookupMobile}
                  disabled={looking}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 rounded-xl text-sm font-semibold transition disabled:opacity-50"
                >
                  {looking ? '...' : 'بحث'}
                </button>
              </div>
              {found && (
                <p className="text-xs text-emerald-600 mt-1.5 font-medium">✓ عميل مسجل — تم ملء الاسم تلقائياً</p>
              )}
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                اسم المسافر الرباعي *
                {found && <span className="text-xs text-gray-400 font-normal mr-1">(قابل للتعديل)</span>}
              </label>
              {/* Guidance banner */}
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-2">
                <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-xs text-amber-700 leading-relaxed">
                  أدخل <strong>الاسم الرباعي</strong> كما هو مدوّن في بطاقة الهوية الوطنية أو جواز السفر
                  <span className="block mt-0.5 text-amber-600">مثال: أحمد سالم محمد عبدالله</span>
                </p>
              </div>
              <input
                className={`w-full border rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 transition ${
                  pName && !nameValid
                    ? 'border-red-300 focus:ring-red-400 bg-red-50'
                    : nameValid
                    ? 'border-emerald-300 focus:ring-emerald-400'
                    : 'focus:ring-blue-400'
                }`}
                placeholder="الاسم الأول · اسم الأب · اسم الجد · اسم العائلة"
                value={pName}
                onChange={(e) => setPName(e.target.value)}
              />
              {/* Live word counter / validation feedback */}
              <div className="flex items-center justify-between mt-1.5">
                {pName && !nameValid ? (
                  <p className="text-xs text-red-600 font-medium">
                    أدخلت {nameParts.length} {nameParts.length === 1 ? 'اسم' : 'أسماء'} — مطلوب 4 أسماء على الأقل
                  </p>
                ) : nameValid ? (
                  <p className="text-xs text-emerald-600 font-medium">✓ الاسم الرباعي مكتمل</p>
                ) : (
                  <p className="text-xs text-gray-400">أدخل 4 أسماء على الأقل</p>
                )}
                <p className="text-xs text-gray-400">{nameParts.length} / 4</p>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-xl p-3 text-sm">
              <p className="text-gray-600 font-medium mb-1">ملخص الحجز</p>
              <p className="text-gray-700">{trip?.fromCity} ← {trip?.toCity}</p>
              <p className="text-gray-500 text-xs">{fmtDate(trip?.departureTime)}</p>
              <p className="text-gray-700 mt-1">المقاعد: {[...selected].sort((a,b)=>a-b).join('، ')}</p>
              <p className="font-bold text-blue-700 mt-1">{totalAmount.toLocaleString()} ج.س</p>
            </div>
          </div>

          <button
            onClick={confirmBooking}
            disabled={submitting || !nameValid || !mobile}
            className="w-full mt-4 bg-emerald-600 text-white py-3 rounded-xl font-bold hover:bg-emerald-700 disabled:opacity-40 transition"
          >
            {submitting ? 'جاري إصدار التذكرة...' : 'تأكيد الحجز وإصدار التذكرة'}
          </button>
        </div>
      )}

      {/* ── Step 3: Payment ─── */}
      {step === 3 && booking && (
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">الدفع وإرسال الإيصال</h2>
          <p className="text-sm text-gray-500 mb-5">رقم الحجز: <span className="font-mono font-bold text-blue-700">{booking.bookingNumber}</span></p>

          {/* Amount summary */}
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mb-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-blue-600 font-medium">إجمالي المبلغ المطلوب تحويله</p>
              <p className="text-2xl font-bold text-blue-800">{parseFloat(booking.totalAmount).toLocaleString()} <span className="text-sm font-normal">ج.س</span></p>
            </div>
            <div className="text-left">
              <p className="text-xs text-emerald-600">عمولتك</p>
              <p className="font-bold text-emerald-600">{parseFloat(booking.commissionAmount).toLocaleString()} ج.س</p>
            </div>
          </div>

          {/* Bank accounts */}
          {bankAccounts.length > 0 ? (
            <div className="bg-white rounded-2xl border p-4 mb-4">
              <p className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                حسابات الشركة البنكية
              </p>
              <div className="space-y-3">
                {bankAccounts.map((b, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                    <p className="font-bold text-gray-800 text-sm">{b.bankName}</p>
                    <p className="text-xs text-gray-500 mt-0.5">صاحب الحساب: <span className="font-medium text-gray-700">{b.accountHolder}</span></p>
                    <p className="font-mono text-base font-bold text-blue-700 mt-1 tracking-wider">{b.accountNumber}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4 text-sm text-amber-800">
              <p className="font-bold mb-1">تنبيه</p>
              <p>لم تقم الشركة بإضافة حسابات بنكية بعد — تواصل معها مباشرة للحصول على تفاصيل التحويل.</p>
            </div>
          )}

          {/* Upload slip */}
          <div className="bg-white rounded-2xl border p-4 mb-4">
            <p className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
              <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              رفع إيصال الدفع
            </p>
            <label className={`flex flex-col items-center justify-center w-full border-2 border-dashed rounded-xl p-5 cursor-pointer transition ${slip ? 'border-emerald-400 bg-emerald-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}>
              {slipPreview ? (
                <img src={slipPreview} alt="إيصال" className="max-h-40 rounded-lg object-contain mb-2" />
              ) : slip ? (
                <svg className="w-10 h-10 text-emerald-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              ) : (
                <svg className="w-10 h-10 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              )}
              <span className={`text-sm font-medium ${slip ? 'text-emerald-700' : 'text-gray-500'}`}>
                {slip ? slip.name : 'اضغط لاختيار صورة أو PDF للإيصال'}
              </span>
              <span className="text-xs text-gray-400 mt-1">JPG · PNG · PDF — حد أقصى 5 ميجا</span>
              <input type="file" accept="image/jpeg,image/png,application/pdf" className="hidden" onChange={handleSlipChange} />
            </label>
          </div>

          <button
            onClick={submitPayment}
            disabled={uploadingSlip || !slip}
            className="w-full bg-emerald-600 text-white py-3 rounded-xl font-bold hover:bg-emerald-700 disabled:opacity-40 transition"
          >
            {uploadingSlip ? 'جاري الإرسال...' : 'إرسال إيصال الدفع للمراجعة'}
          </button>
        </div>
      )}

      {/* ── Step 4: Pending review ─── */}
      {step === 4 && booking && (
        <div className="text-center">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">قيد المراجعة</h2>
          <p className="text-gray-500 text-sm mb-1">تم استلام إيصال الدفع بنجاح</p>
          <p className="text-gray-500 text-sm mb-6">ستقوم الشركة بمراجعة الإيصال وإرسال التذكرة على واتساب المسافر بعد التأكيد</p>

          <div className="bg-white rounded-2xl border p-6 text-right mb-5">
            <p className="text-xs text-gray-400 mb-1">رقم الحجز</p>
            <p className="text-2xl font-mono font-bold text-blue-700 mb-4">{booking.bookingNumber}</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-gray-400 text-xs">المبلغ</p><p className="font-bold">{parseFloat(booking.totalAmount).toLocaleString()} ج.س</p></div>
              <div><p className="text-gray-400 text-xs">عمولة المكتب</p><p className="font-bold text-emerald-600">{parseFloat(booking.commissionAmount).toLocaleString()} ج.س</p></div>
              <div><p className="text-gray-400 text-xs">المقاعد</p><p className="font-bold">{booking.seats?.join('، ')}</p></div>
              <div><p className="text-gray-400 text-xs">الحالة</p><p className="font-bold text-amber-600">بانتظار المراجعة</p></div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-5 text-sm text-blue-700 text-right">
            <p>📱 سيتلقى المسافر رسالة واتساب تفيد باستلام طلب الحجز — وستُرسل التذكرة فور تأكيد الشركة للدفع.</p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => {
                setStep(0); setFrom(''); setTo(''); setDate(''); setTrips([]);
                setTrip(null); setSeatData(null); setSelected([]);
                setMobile(''); setPName(''); setFound(null);
                setBooking(null); setBankAccounts([]); setSlip(null); setSlipPreview(null);
              }}
              className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition"
            >
              حجز جديد
            </button>
            <button onClick={() => navigate('/office/bookings')} className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-xl font-bold hover:bg-gray-50 transition">
              قائمة الحجوزات
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
